import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Link,
  Stack,
  SxProps,
  TextField,
  Typography,
} from "@mui/material";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { CopyContentIcon } from "../../Common/assets/Icons";
import ConfirmRegeneration from "../ConfirmRegeneration";

type CredProps = {
  clientId: string;
  clientSecret?: string;
};
const DiskStyle: SxProps = {
  height: "10px",
  width: "10px",
  borderRadius: "50%",
  bgcolor: "text.primary",
};

const OAuth = () => {
  const [credData, setCredData] = useState<CredProps | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [link, setLink] = useState<string>("");

  useEffect(() => {
    async function getOAuthData() {
      try {
        setLoading(true);
        let res: AxiosResponse = await http.get("oauth/credentials");
        let res2: AxiosResponse = await http.get("data/docs");
        const externalLink = res2?.data?.data?.filter(
          (item: any) => item?.type === "externalApi"
        )?.[0]?.url;

        const data = res?.data?.data;
        const newData = {
          clientId: data?.clientId || "",
        };
        setCredData(newData);
        setLink(externalLink);
      } catch (e) {
        errorToastMessage(e as Error);
      } finally {
        setLoading(false);
      }
    }
    getOAuthData();
  }, []);

  async function generateAPICredentials() {
    if (!credData?.clientId) {
      try {
        setLoading(true);
        let res: AxiosResponse = await http.post("oauth/credentials/generate");
        const data = res.data.data;
        const newData = {
          clientId: data?.clientId,
          clientSecret: data?.clientSecret,
        };
        setCredData(newData);
      } catch (e) {
        errorToastMessage(e as Error);
      } finally {
        setLoading(false);
      }
    } else {
      setShowConfirmation(true);
    }
  }
  function closeModal() {
    setShowConfirmation(false);
  }
  return (
    <Box
      sx={{
        p: 2.5,
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: "0px 0px 12px 0px rgba(0, 0, 0, 0.08)",
      }}
    >
      <Typography variant="h2" fontWeight="regular" color="text.primary">
        Mahalo Research API Credentials
      </Typography>
      <Box marginTop={3}>
        {!loading && !credData?.clientId && (
          <Typography variant="subtitle1" fontWeight="regular">
            Your API credentials have not yet been generated.
          </Typography>
        )}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && credData?.clientId && (
          <FormControl sx={InputWrapper}>
            <FormLabel sx={LabelStyle}>Client ID</FormLabel>
            <TextField
              InputProps={{
                endAdornment: (
                  <IconButton
                    edge="end"
                    onClick={() => {
                      navigator.clipboard.writeText(credData?.clientId);
                      toastMessage("success", "Copied Client ID to clipboard.");
                    }}
                  >
                    <CopyContentIcon />
                  </IconButton>
                ),
                readOnly: true,
              }}
              value={credData?.clientId}
              sx={{ width: "70ch" }}
            />
          </FormControl>
        )}
        {!loading && credData?.clientSecret && (
          <FormControl sx={{ ...InputWrapper, mt: 2.5 }}>
            <FormLabel sx={LabelStyle}>Client Secret</FormLabel>
            <TextField
              InputProps={{
                endAdornment: (
                  <IconButton
                    edge="end"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        credData?.clientSecret || ""
                      );
                      toastMessage(
                        "success",
                        "Copied Client Secret to clipboard."
                      );
                    }}
                  >
                    <CopyContentIcon />
                  </IconButton>
                ),
                readOnly: true,
              }}
              value={credData?.clientSecret}
              sx={{ width: "70ch" }}
            />
          </FormControl>
        )}
      </Box>

      {!loading && (
        <Button
          variant="contained"
          sx={{ mt: 3 }}
          onClick={generateAPICredentials}
        >
          {credData?.clientId
            ? "Regenerate Credentials"
            : "Generate Credentials"}
        </Button>
      )}
      {showConfirmation && (
        <ConfirmRegeneration
          openModal={showConfirmation}
          closeModal={closeModal}
          setCredData={setCredData}
        />
      )}

      {!loading && (
        <Stack gap={2}>
          <Typography
            variant="h2"
            fontWeight="regular"
            color="text.primary"
            mt={3}
          >
            What is the Mahalo EDC API?
          </Typography>
          <Typography variant="subtitle1" color="text.primary">
            Details about the API can be found in the{" "}
            <Link href={link} target="_blank" rel="noopener noreferrer">
              API documentation.
            </Link>
          </Typography>
          <Typography variant="subtitle1" color="text.primary">
            You can do things like:
          </Typography>
          <Stack gap={1} ml={4.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                Data point collection and participants management
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                Accessing and interacting with available fields
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                Accessing and interacting with visits and forms etc.
              </Typography>
            </Box>
          </Stack>
          <Typography
            variant="h2"
            fontWeight="regular"
            color="text.primary"
            mt={3}
          >
            Important
          </Typography>
          <Stack gap={1} ml={4.5}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                Never share these credentials with anyone as they grant access
                to your account
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                These credentials grant the same permissions as your personal
                account
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={DiskStyle} />
              <Typography variant="subtitle1" color="text.primary">
                Regenerating credentials will revoke access for previously
                generated ones
              </Typography>
            </Box>
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default OAuth;
